import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import DOMPurify from 'dompurify';
import {
  ConfigContext,
  formatCurrencyAmount,
} from './utils/Config';
import { CloseIcon, ArrowUpIcon, ArrowDownIcon } from '@chakra-ui/icons';
import { LayoutList, List } from 'lucide-react';
import {
  Table, Tbody, Tr, Th, Td, Text, Box, Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, Spinner, Link, Flex, Alert, AlertIcon, AlertTitle, AlertDescription,
  Tabs, TabList, TabPanels, Tab, TabPanel, Select, Tooltip, Hide, Show, Stack, Card, CardBody, StackDivider
} from '@chakra-ui/react';
import MyCredit from './MyCredit';
import { useTranslation } from 'react-i18next';
import { format, fromUnixTime } from 'date-fns';
import SaleSummary from './utils/SaleSummary';
import GroupSummary from './utils/GroupSummary';
import GroupEditor from './utils/GroupEditor';
import MyMaxBidLimit from './MyMaxBidLimit';
import ImageGallery from './utils/ImageGallery';
import { ItemLinkChakra } from './utils/ItemLink';

const MyBids = () => {
  const { t, i18n } = useTranslation();
  const [userData, setUserData] = useState(null);
  const [itemsBidData, setItemsBidData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupItemId] = useState(null);
  const popupRef = useRef(null);
  const config = useContext(ConfigContext);
  const [itemDetails, setItemDetails] = useState({});
  const [activeSales, setActiveSales] = useState([]);
  const [pastSales, setPastSales] = useState([]);
  const [selectedPastSale, setSelectedPastSale] = useState(null);
  const [pastSaleBids, setPastSaleBids] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [activeSaleFilter, setActiveSaleFilter] = useState('all');
  const [pastSaleFilter, setPastSaleFilter] = useState('all');
  const [expandedLots, setExpandedLots] = useState({});
  const [viewType, setViewType] = useState('full');

  const fetchItemDetails = useCallback(
    async (itemUuids) => {
      if (itemUuids.length === 0) return;
      const languageForServer = i18n.language === 'zh' ? 'zh-hans' : i18n.language;
      try {
        const response = await fetch(`${config.siteData.backoffice_url}/api/items-json`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            json_type: 'web',
            uuids: itemUuids,
            language: languageForServer
          }),
        });
  
        if (!response.ok) {
          throw new Error(response.statusText || t('myBids.networkResponseError'));
        }
  
        const itemDetailsData = await response.json();
        setItemDetails(prevDetails => ({
          ...prevDetails,
          ...itemDetailsData.data.reduce((acc, item) => {
            acc[item.uuid] = item;
            return acc;
          }, {})
        }));
      } catch (error) {
        setError(error.message);
      }
    },
    [config, i18n.language, t]
  ); 

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        handleClosePopup();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!config.accessToken) {
      setError(t('myBids.navigateToSaleOrItemPage'));
      setIsLoading(false);
      return;
    }

    if (config.userData.accessToken) {
      setUserData(config.userData);
    } else {
      // If somehow userData is not in context, fetch it using the context's fetchUserData
      config.fetchUserData().then(() => {
        if (config.userData) {
          setUserData(config.userData);
        }
      }).catch(error => {
        setError(error.message);
        setIsLoading(false);
      });
    }
  }, [config, t]);

  useEffect(() => {
    if (!userData || !userData.website_bidder_info) return;
    const saleUuids = userData.website_bidder_info.map(info => info.sale_uuid);

    Promise.all(saleUuids.map((uuid, index) =>
      fetch(`${config.backendUrl}/api/items?sort=lot&only_user_bids=1&show_all_bids=1&placed_bids_also_rejected=1&sale_uuid=${uuid}&page=1&access_token=${config.accessToken}`)
        .then(response => {
          if (!response.ok) {
            throw new Error(response.title || t('myBids.networkResponseError'));
          }
          return response.json().then(data => ({
            ...data,
            sale: userData.website_bidder_info[index],
          }));
        })
    ))
      .then(salesResponses => {
        const active = [];
        const past = [];
        salesResponses.forEach(saleResponse => {
          if (saleResponse.sale.sale_status !== 'closed_ended' && saleResponse.sale.sale_status !== 'closed_not_started') {
            active.push(saleResponse);
          } else if (saleResponse.sale.sale_status === 'closed_ended') {
            past.push(saleResponse);
          }
        });
        setItemsBidData(active);
        setActiveSales(active.map(sale => sale.sale));
        setPastSales(past.map(sale => sale.sale));

        // Fetch item details for active sales
        const activeItemUuids = active.flatMap(sale =>
          sale.data.map(item => item.uuid)
        );
        fetchItemDetails(activeItemUuids);

        setIsLoading(false);
      })
      .catch(error => {
        setError(error.message);
        setIsLoading(false);
      });
  }, [userData, config, t, i18n.language, fetchItemDetails]);

  const handlePastSaleChange = async (event) => {
    const value = event.target.value;
  
    if (!value) {
      setSelectedPastSale(null);
      setPastSaleBids(null);
      return;
    }

    const saleUuid = value;
    const selectedSale = pastSales.find(sale => sale.sale_uuid === saleUuid);
    setSelectedPastSale(formatSaleTitle(selectedSale));
    setIsLoading(true);

    try {
      const response = await fetch(`${config.backendUrl}/api/items?sort=lot&only_user_bids=1&show_all_bids=1&placed_bids_also_rejected=1&sale_uuid=${saleUuid}&page=1&access_token=${config.accessToken}`);
      if (!response.ok) {
        throw new Error(response.title || t('myBids.networkResponseError'));
      }
      const data = await response.json();

      // Process the data similar to how we process active sales
      const groupedBids = data.data.reduce((acc, item) => {
        const combinedBids = [
          ...(item.bid || []),
          ...(item.book_bid || []),
          ...(item.agent_bid || [])
        ].map(bid => ({
          ...bid,
          lot: item.lot,
          item_uuid: item.uuid
        }));

        if (!acc[item.lot]) {
          acc[item.lot] = {
            item: item,
            bids: [],
            status: item.status,
            current_price: item.current_price
          };
        }
        acc[item.lot].bids = acc[item.lot].bids.concat(combinedBids);
        return acc;
      }, {});

      Object.values(groupedBids).forEach(lot => {
        lot.bids.sort((a, b) => new Date(b.created) - new Date(a.created));
      });

      const matchingSale = pastSales.find(sale => sale.sale_uuid === saleUuid);

      setPastSaleBids({
        ...data,
        groupedBids,
        sale: {
          ...matchingSale,
          title: selectedSale
        }
      });

      // Fetch item details for the selected past sale
      const itemUuids = data.data.map(item => item.uuid).filter(uuid => uuid);
      await fetchItemDetails(itemUuids);

      setActiveTab(1); // Ensure we stay on the past sales tab

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const sortBids = (bids) => {
    return [...bids].sort((a, b) => {
      return new Date(b.created) - new Date(a.created);
    });
  };

  const getHighestValidBid = (bids) => {
    if (!bids || bids.length === 0) return null;
    
    const validBids = bids.filter(bid => !isInvalidBid(bid))
                         .sort((a, b) => b.amount - a.amount);
    
    if (validBids.length > 0) {
      return validBids[0];
    }
    
    const allBids = [...bids].sort((a, b) => b.amount - a.amount);
    return allBids[0];
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  if (isLoading) {
    return (
      <Flex
        width="100vw"
        height="100vh"
        justifyContent="center"
        alignItems="center"
      >
        <Spinner color="blue.500" size="xl" thickness="4px" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Alert status='error'>
        <AlertIcon />
        <AlertTitle>{t('myBids.error')}</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const processedSalesData = itemsBidData
  .filter(saleGroup =>
    saleGroup.sale.sale_status !== 'closed_ended' &&
    saleGroup.sale.sale_status !== 'closed_not_started'
  )
  .map((saleGroup) => {
    const groupedBids = saleGroup.data.reduce((acc, item) => {
      const combinedBids = [
        ...(item.bid || []),
        ...(item.book_bid || []),
        ...(item.agent_bid || [])
      ].map(bid => ({
        ...bid,
        lot: item.lot,
        item_uuid: item.uuid
      }));

      if (!acc[item.lot]) {
        acc[item.lot] = {
          item: item,
          bids: []
        };
      }
      acc[item.lot].bids = acc[item.lot].bids.concat(combinedBids);
      return acc;
    }, {});

    if (
      Object.keys(groupedBids).length === 0 &&
      (saleGroup.sale.sale_status === 'closed_ended' || saleGroup.sale.sale_status === 'closed_not_started')
    ) {
      return null;
    }

    return {
      ...saleGroup,
      groupedBids,
    };
  }).filter(Boolean);

  const formatDate = (dateString) => {
    const options = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    };
    const date = new Date(dateString);
    return date.toLocaleString(i18n.language, options);
  };

  const formatBidType = (type) => {
    return type.replace('_', ' ');
  };

  const LotInfo = ({ lotData, currency, isActiveSale, isRegularAuction }) => {
    const currentPrice = formatCurrencyAmount(lotData.item.current_price, currency);
    const winningBid = lotData.bids.find(bid => bid.id === lotData.item.winning_bid);
    const isWinner = winningBid !== undefined;
    const isSold = lotData.item.status === 'sold';

    return (
      <Box className="lot-status">
        {isActiveSale && isRegularAuction && !isSold ? (
          <>
            <Box>{t('myBids.currentPrice', { price: currentPrice })}</Box>
            {isWinner && (
              <Box className="won-item" mt={1}>
                {t('myBids.youAreHighestBidder')}
              </Box>
            )}
          </>
        ) : (
          lotData.item.status === 'sold' ? ( 
            <>
              <Box>{t('myBids.soldFor', { price: currentPrice })}</Box>
              {isWinner && (
                <Box className="won-item" mt={1}>
                  {t('myBids.youWonThisLot')}
                </Box>
              )}
            </>
          ) : lotData.item.status === 'unsold' ? (
            <Box>{t('myBids.unsold')}</Box>
          ) : null
        )}
      </Box>
    );
  };

  const handleActiveSaleFilterChange = (event) => {
    setActiveSaleFilter(event.target.value);
  };

  const handlePastSaleFilterChange = (event) => {
    setPastSaleFilter(event.target.value);
  };

  const handleGroupUpdate = (bidId, newGroupName) => {
    const updateBidsInData = (data) => {
      // First find all bids in the same group to update their alternative_bids
      const allGroupBids = [];
      data.forEach(saleGroup => {
        saleGroup.data.forEach(item => {
          [...(item.bid || []), ...(item.book_bid || []), ...(item.agent_bid || [])]
            .forEach(bid => {
              if (bid.bids_group_name === newGroupName || bid.id === bidId) {
                allGroupBids.push({
                  id: bid.id,
                  lot: item.lot,
                  item_nid: item.id
                });
              }
            });
        });
      });
  
      // Update the data with new group name and alternative_bids
      return data.map(saleGroup => ({
        ...saleGroup,
        data: saleGroup.data.map(item => ({
          ...item,
          bid: item.bid?.map(b => {
            if (b.id === bidId) {
              return {
                ...b,
                bids_group_name: newGroupName,
                alternative_bids: newGroupName ? allGroupBids.filter(gb => gb.id !== bidId) : null
              };
            }
            if (b.bids_group_name === newGroupName) {
              return {
                ...b,
                alternative_bids: allGroupBids.filter(gb => gb.id !== b.id)
              };
            }
            return b;
          }),
          book_bid: item.book_bid?.map(b => {
            if (b.id === bidId) {
              return {
                ...b,
                bids_group_name: newGroupName,
                alternative_bids: newGroupName ? allGroupBids.filter(gb => gb.id !== bidId) : null
              };
            }
            if (b.bids_group_name === newGroupName) {
              return {
                ...b,
                alternative_bids: allGroupBids.filter(gb => gb.id !== b.id)
              };
            }
            return b;
          }),
          agent_bid: item.agent_bid?.map(b => {
            if (b.id === bidId) {
              return {
                ...b,
                bids_group_name: newGroupName,
                alternative_bids: newGroupName ? allGroupBids.filter(gb => gb.id !== bidId) : null
              };
            }
            if (b.bids_group_name === newGroupName) {
              return {
                ...b,
                alternative_bids: allGroupBids.filter(gb => gb.id !== b.id)
              };
            }
            return b;
          })
        }))
      }));
    };
  
    setItemsBidData(prevData => updateBidsInData(prevData));
    
    if (pastSaleBids) {
      setPastSaleBids(prev => ({
        ...prev,
        data: updateBidsInData([{ data: prev.data }])[0].data
      }));
    }
  };

  const formatSaleTitle = (sale) => {
    if (!sale.sale_date?.value || !sale.sale_date?.value2) {
      return sale.sale_title;
    }
  
    const startDate = fromUnixTime(parseInt(sale.sale_date.value));
    const endDate = fromUnixTime(parseInt(sale.sale_date.value2));
  
    const startMonth = format(startDate, 'MMM');
    const endMonth = format(endDate, 'MMM');
    const startDay = format(startDate, 'd');
    const endDay = format(endDate, 'd');
    const startYear = format(endDate, 'yyyy');
  
    let dateString;
    if (format(startDate, 'MM.yyyy') === format(endDate, 'MM.yyyy')) {
      // Same month and year
      if (startDay === endDay) {
        // Same day
        dateString = `${startMonth}. ${startDay}, ${startYear}`;
      } else {
        // Different days
        dateString = `${startMonth} ${startDay}-${endDay}, ${startYear}`;
      }
    } else {
      // Different months
      dateString = `${startMonth}. ${startDay} - ${endMonth}. ${endDay}, ${startYear}`;
    }
  
    return (
      `${sale.sale_title} - <span class="sale-date">${dateString}</span>`
    );
  };

  const getFilteredLots = (lots, isActiveSale) => {
    const filter = isActiveSale ? activeSaleFilter : pastSaleFilter;
    switch (filter) {
      case 'highest':
        return Object.entries(lots).filter(([_, lotData]) =>
          lotData.bids.some(bid => bid.id === lotData.item.winning_bid)
        );
      case 'not-highest':
        return Object.entries(lots).filter(([_, lotData]) =>
          !lotData.bids.some(bid => bid.id === lotData.item.winning_bid)
        );
      case 'won':
        return Object.entries(lots).filter(([_, lotData]) =>
          lotData.item.status === 'sold' && lotData.bids.some(bid => bid.id === lotData.item.winning_bid)
        );
      case 'lost':
        return Object.entries(lots).filter(([_, lotData]) =>
          lotData.item.status === 'unsold' ||
          (lotData.item.status === 'sold' && !lotData.bids.some(bid => bid.id === lotData.item.winning_bid))
        );
      default:
        return Object.entries(lots);
    }
  };

  const isDuplicatedBid = (bid) => {
    return bid.status === 'accepted_duplicated_amount';
  };

  const getDuplicatedReason = (bid) => {
    if (bid.status === 'accepted_duplicated_amount') {
      return t('myBids.duplicatedAmountTooltip'); 
    }
    return null;
  };

  const isInvalidBid = (bid) => {
    const rejectedStatuses = [
      'rejected_bidder_corrected',
      'rejected_over_bidder_max',
      'rejected_over_credit_limit',
      'rejected_mail_after_last_floor',
      'rejected_bellow_minimum'
    ];
    
    return bid.deleted || rejectedStatuses.includes(bid.status);
  };
  
  const getInvalidReason = (bid) => {
    if (bid.deleted) {
      return t('myBids.deleted');
    }
    if (bid.status && bid.status.startsWith('rejected_')) {
      const reason = bid.status
        .replace('rejected_', '')
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return reason;
    }
    return null;
  };

  return (
    <>
      <div className="my-bids">
        <h1 className="page-header">{t('myBids.header')}</h1>

        <Tabs index={activeTab} onChange={(index) => setActiveTab(index)}>
          <TabList>
            <Tab>{t('myBids.activeSales')}</Tab>
            <Tab>{t('myBids.pastSales')}</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {processedSalesData.length === 0 ? (
                <Box textAlign="center" fontSize="xl" mt={8}>
                  {t('myBids.noActiveSales')}
                </Box>
              ) : (
                <>
                  {processedSalesData.some(saleGroup => saleGroup.sale.auction_type === 'regular') && (
                    <Select value={activeSaleFilter} onChange={handleActiveSaleFilterChange} mb={4}>
                      <option value="all">{t('myBids.filterAll')}</option>
                      <option value="highest">{t('myBids.filterHighest')}</option>
                      <option value="not-highest">{t('myBids.filterNotHighest')}</option>
                    </Select>
                  )}
                  
                  <Accordion allowMultiple defaultIndex={[...Array(activeSales.length).keys()]}>
                    {processedSalesData.map((saleGroup, index) => (
                      <AccordionItem key={index} onChange={(isExpanded) => {
                        if (isExpanded) {
                          const itemUuids = Object.values(saleGroup.groupedBids).map(lotData => lotData.item.uuid);
                          fetchItemDetails(itemUuids);
                        }
                      }}>
                        <AccordionButton>
                          <Box as="span" flex='1' textAlign='left' fontSize="xl" dangerouslySetInnerHTML={{ 
                            __html: formatSaleTitle(saleGroup.sale)
                          }} />
                          <AccordionIcon />
                        </AccordionButton>
                        <AccordionPanel pb={4}>
                          <div className="sale-summary">
                            <SaleSummary 
                              groupedBids={saleGroup.groupedBids}
                              currency={saleGroup.sale.currency}
                              isActive={true}
                              itemDetails={itemDetails}
                            />
                          </div>
                          <GroupSummary groupedBids={saleGroup.groupedBids} />
                          
                          <Flex gap={4} ml={4} mb={4}>
                          <Link 
                            onClick={() => setViewType('full')}
                            color={viewType === 'full' ? 'blue.500' : 'gray.500'}
                            textDecoration={viewType === 'full' ? 'underline' : 'none'}
                            cursor="pointer"
                            display="flex"
                            alignItems="center"
                            gap={2}
                          >
                            <LayoutList size={16} /> {t('myBids.detailedView')}
                          </Link>
                          <Link 
                            onClick={() => setViewType('table')}
                            color={viewType === 'table' ? 'blue.500' : 'gray.500'}
                            textDecoration={viewType === 'table' ? 'underline' : 'none'}
                            cursor="pointer"
                            display="flex"
                            alignItems="center"
                            gap={2}
                          >
                            <List size={16} /> {t('myBids.summaryView')}
                          </Link>
                          </Flex>
                          <Box p={4} borderWidth="1px" borderRadius="xs">
                            {Object.keys(saleGroup.groupedBids).length === 0 ? (
                              <Box className="no-bids" mb={4}>
                                <Text size="xl">{t('myBids.noBidsPlaced')}</Text>
                              </Box>
                            ) : viewType === 'full' ? (
                              getFilteredLots(saleGroup.groupedBids, true).map(([lot, lotData], lotIndex) => (
                                <Box key={lotIndex} className="lot-info" mb={4}>
                                  <Box className="lot-number">
                                  <ItemLinkChakra itemUuid={lotData.item.uuid} color="blue.500">
                                    {t('myBids.lot')} {lot}
                                  </ItemLinkChakra>
                                  </Box>
                                  <Box className="lot-price">
                                    {t('myBids.openingBid', { amount: itemDetails[lotData.item.uuid]?.opening_price })}
                                  </Box>
                                  <Box className="current-price">
                                  <LotInfo
                                    lotData={lotData}
                                    currency={saleGroup.sale.currency}
                                    isActiveSale={true}
                                    isRegularAuction={saleGroup.sale.auction_type === 'regular'}
                                  />
                                  </Box>
                                  <Box className="lot-image">
                                    <ImageGallery imageGallery={itemDetails[lotData.item.uuid]?.image_gallery} />
                                  </Box>
                                  <Box className="lot-details">
                                    <Box className="lot-title">{itemDetails[lotData.item.uuid]?.has_title ? itemDetails[lotData.item.uuid]?.title : ''}</Box>
                                    <Box>
                                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(itemDetails[lotData.item.uuid]?.body) }}></div>
                                    </Box>
                                  </Box>
                                  <Box className="lot-bids">
                                    <Table>
                                      <Tbody>
                                        {(expandedLots[lotData.item.uuid] ? sortBids(lotData.bids) : sortBids(lotData.bids).slice(0, 3)).map((bid, bidIndex) => (
                                          <Tr key={bidIndex}>
                                            <Td>
                                              <span className={isInvalidBid(bid) ? 'line-through' : ''}>
                                                {formatCurrencyAmount(bid.amount, saleGroup.sale.currency)}
                                              </span>
                                              {isInvalidBid(bid) && (
                                                <Tooltip label={getInvalidReason(bid)} hasArrow>
                                                  <span className="deleted-label">{t('myBids.deleted')}</span>
                                                </Tooltip>
                                              )}
                                              {isDuplicatedBid(bid) && (
                                                <Tooltip label={getDuplicatedReason(bid)} hasArrow>
                                                  <span className="deleted-label">
                                                    {t('myBids.duplicateBid')}
                                                  </span>
                                                </Tooltip>
                                              )}
                                            </Td>
                                            <Td className="bid-type">
                                                ({formatBidType(bid.type)} #{bid.bidder_id})
                                                &nbsp;
                                              <span className="bid-date">{formatDate(bid.created)}</span>
                                            </Td>
                                          </Tr>
                                        ))}
                                      </Tbody>
                                    </Table>                                    
                                    {lotData.bids.length > 3 && (
                                      <Box onClick={() => setExpandedLots(prev => ({...prev, [lotData.item.uuid]: !prev[lotData.item.uuid]}))}
                                        cursor="pointer"
                                        textAlign="center"
                                        mt={2}>
                                        {expandedLots[lotData.item.uuid] ?
                                          <>{t('myBids.close')} <ArrowUpIcon /></> :
                                          <>{t('myBids.showMore')} <ArrowDownIcon /></>
                                        }
                                      </Box>
                                    )}
                                    <Box mt={2}>
                                      {lotData.bids.find(b => b.type === 'mail') && (
                                        <GroupEditor
                                          bid={lotData.bids.find(b => b.type === 'mail')}
                                          item={lotData.item}
                                          onGroupUpdate={handleGroupUpdate}
                                          itemsBidData={itemsBidData}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              ))
                            ) : (
                              <>
                                <Hide below='md'>
                                  <Box overflowX="auto" width="100%">
                                    <Table size={{base: "sm", md: "md"}}>
                                      <thead>
                                        <Tr>
                                          <Th>{t('myBids.lotNumber')}</Th>
                                          <Th>{t('myBids.openingPrice')}</Th>
                                          <Th>{t('myBids.topBid')}</Th>                                    
                                          <Th>{t('myBids.bidderNumber')}</Th>
                                          <Th>{t('myBids.group')}</Th>
                                          <Th>{t('myBids.soldFortable')}</Th>      
                                          <Th></Th>
                                          </Tr>
                                      </thead>   
                                      <Tbody>
                                        {getFilteredLots(saleGroup.groupedBids, true).map(([lot, lotData]) => {
                                          const winningBid = lotData.bids.find(bid => bid.id === lotData.item.winning_bid);
                                          const isWinner = winningBid !== undefined;
                                          const topBid = getHighestValidBid(lotData.bids);
                                          return (
                                            <Tr key={lot} className="lot-table">
                                              <Td>
                                                <ItemLinkChakra itemUuid={lotData.item.uuid} color="blue.500">
                                                  {lot}
                                                </ItemLinkChakra>
                                              </Td>
                                              <Td>{itemDetails[lotData.item.uuid]?.opening_price}</Td>                                      
                                              <Td>
                                                <span className={isInvalidBid(topBid) ? 'line-through' : ''}>
                                                  {formatCurrencyAmount(topBid?.amount || 0, saleGroup.sale.currency)}
                                                </span>
                                                {isInvalidBid(topBid) && 
                                                  <Tooltip label={getInvalidReason(topBid)} hasArrow>
                                                    <span className="deleted-label">{t('myBids.deleted')}</span>
                                                  </Tooltip>
                                                }
                                                {isDuplicatedBid(topBid) && (
                                                  <Tooltip label={getDuplicatedReason(topBid)} hasArrow>
                                                    <span className="deleted-label">
                                                      {t('myBids.duplicateBid')}
                                                    </span>
                                                  </Tooltip>
                                                )}
                                              </Td>
                                              <Td className="bidder-number">#{topBid?.bidder_id}</Td>
                                              <Td>
                                                {lotData.bids.find(b => b.type === 'mail') && (
                                                  <GroupEditor 
                                                    bid={lotData.bids.find(b => b.type === 'mail')} 
                                                    item={lotData.item}
                                                    onGroupUpdate={handleGroupUpdate}
                                                    itemsBidData={itemsBidData}
                                                  />
                                                )}
                                              </Td>
                                              <Td>{lotData.item.status === 'sold' ? formatCurrencyAmount(lotData.item.current_price, saleGroup.sale.currency) : '-'}</Td>
                                              <Td className="won-item">{lotData.item.status === 'sold' ? (isWinner ? t('myBids.youWonThisLot') : null) : lotData.item.status === 'unsold' ? t('myBids.unsold') : isWinner ? t('myBids.youAreHighestBidder') : '-'}</Td>
                                            </Tr>
                                          );
                                        })}
                                      </Tbody>
                                    </Table>
                                  </Box>
                                </Hide>
                                
                                <Show below='md'>
                                  <Stack spacing={4}>
                                    {getFilteredLots(saleGroup.groupedBids, true).map(([lot, lotData]) => {
                                      const winningBid = lotData.bids.find(bid => bid.id === lotData.item.winning_bid);
                                      const isWinner = winningBid !== undefined;
                                      const topBid = getHighestValidBid(lotData.bids);
                                      
                                      return (
                                        <Card key={lot}>
                                          <CardBody>
                                            <Stack divider={<StackDivider />} spacing={3}>
                                              <Flex justify="space-between" align="center">
                                                <ItemLinkChakra itemUuid={lotData.item.uuid} color="blue.500">
                                                  {t('myBids.lot')} {lot}
                                                </ItemLinkChakra>
                                                <Text>#{topBid?.bidder_id}</Text>
                                              </Flex>
                                              
                                              <Flex justify="space-between" align="center">
                                                <Box fontWeight="medium">{t('myBids.openingPrice')}:</Box>
                                                <Box>{itemDetails[lotData.item.uuid]?.opening_price}</Box>
                                              </Flex>

                                              <Flex justify="space-between" align="center">
                                                <Box fontWeight="medium">{t('myBids.topBid')}:</Box>
                                                <Flex align="center" gap={2}>
                                                  <span className={isInvalidBid(topBid) ? 'line-through' : ''}>
                                                    {formatCurrencyAmount(topBid?.amount || 0, saleGroup.sale.currency)}
                                                  </span>
                                                  {isInvalidBid(topBid) && (
                                                    <Tooltip label={getInvalidReason(topBid)} hasArrow>
                                                      <span className="deleted-label">{t('myBids.deleted')}</span>
                                                    </Tooltip>
                                                  )}
                                                  {isDuplicatedBid(topBid) && (
                                                    <Tooltip label={getDuplicatedReason(topBid)} hasArrow>
                                                      <span className="deleted-label">
                                                        {t('myBids.duplicateBid')}
                                                      </span>
                                                    </Tooltip>
                                                  )}
                                                </Flex>
                                              </Flex>

                                              <Flex justify="space-between" align="center">
                                                <Box fontWeight="medium">{t('myBids.soldFortable')}:</Box>
                                                <Box>{lotData.item.status === 'sold' ? formatCurrencyAmount(lotData.item.current_price, saleGroup.sale.currency) : '-'}</Box>
                                              </Flex>

                                              {(isWinner || lotData.item.status === 'unsold') && (
                                                <Box color="blue.500" textAlign="right" className="won-item">
                                                  {lotData.item.status === 'sold' ? 
                                                    (isWinner ? t('myBids.youWonThisLot') : null) : 
                                                    lotData.item.status === 'unsold' ? 
                                                      t('myBids.unsold') : 
                                                      isWinner ? t('myBids.youAreHighestBidder') : '-'}
                                                </Box>
                                              )}
                                            </Stack>
                                          </CardBody>
                                        </Card>
                                      );
                                    })}
                                  </Stack>
                                </Show>
                              </>
                            )}

                            {showPopup && (
                              <div ref={popupRef} className="popup">
                                <button onClick={handleClosePopup} className="close-button">
                                  {t('myBids.close')}<CloseIcon />
                                </button>
                                <div id={`${popupItemId}`} className="elm-processed clear">
                                </div>
                              </div>
                            )}
                          </Box>                          
                          {saleGroup.sale.sale_status !== 'closed_not_started' && (
                            <MyCredit
                              accessToken={config.accessToken}
                              userUuid={userData.uuid}
                              saleUuid={saleGroup.sale.sale_uuid}
                              currency={saleGroup.sale.currency}
                            />
                          )}
                          <MyMaxBidLimit
                              accessToken={config.accessToken}
                              saleUuid={saleGroup.sale.sale_uuid}
                              userUuid={userData.uuid}
                          />
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </>
              )}
            </TabPanel>
            <TabPanel>
            <Select 
              placeholder={t('myBids.selectPastSale')} 
              onChange={handlePastSaleChange}
            >
              {pastSales.map((sale, index) => (
                <option 
                  key={index} 
                  value={sale.sale_uuid}
                  dangerouslySetInnerHTML={{ __html: formatSaleTitle(sale) }}
                />
              ))}
            </Select>


            {isLoading && selectedPastSale ? (
                <Flex justify="center" align="center" mt={4}>
                  <Spinner />
                </Flex>
              ) : selectedPastSale && pastSaleBids && pastSaleBids.groupedBids ? (
                <Box mt={4}>
                  <Box className="past-sale-title">
                    <div dangerouslySetInnerHTML={{ __html: selectedPastSale }} />
                  </Box>
                  <div className="sale-summary">
                    <SaleSummary 
                      groupedBids={pastSaleBids.groupedBids}
                      currency={pastSaleBids.sale.currency}
                      isActive={false}
                      itemDetails={itemDetails}  
                    />
                  </div>
                  
                  <GroupSummary groupedBids={pastSaleBids.groupedBids} />
                  <Select value={pastSaleFilter} onChange={handlePastSaleFilterChange} mb={4}>
                    <option value="all">{t('myBids.filterAll')}</option>
                    <option value="won">{t('myBids.filterWon')}</option>
                    <option value="lost">{t('myBids.filterLost')}</option>
                  </Select>
                  <Flex gap={4} ml={4} mb={4}>
                  <Link 
                      onClick={() => setViewType('full')}
                      color={viewType === 'full' ? 'blue.500' : 'gray.500'}
                      textDecoration={viewType === 'full' ? 'underline' : 'none'}
                      cursor="pointer"
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <LayoutList size={16} /> {t('myBids.detailedView')}
                    </Link>
                    <Link 
                      onClick={() => setViewType('table')}
                      color={viewType === 'table' ? 'blue.500' : 'gray.500'}
                      textDecoration={viewType === 'table' ? 'underline' : 'none'}
                      cursor="pointer"
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      <List size={16} /> {t('myBids.summaryView')}
                    </Link>
                    
                  </Flex>   

                  {viewType === 'table' ? (
                    <>
                      <Hide below='md'>
                        <Box overflowX="auto" width="100%">
                          <Table size={{base: "sm", md: "md"}}>
                            <thead>
                              <Tr>
                                <Th>{t('myBids.lotNumber')}</Th>
                                <Th>{t('myBids.openingPrice')}</Th>
                                <Th>{t('myBids.topBid')}</Th>
                                <Th>{t('myBids.bidderNumber')}</Th>
                                <Th>{t('myBids.group')}</Th>
                                <Th>{t('myBids.soldFortable')}</Th>
                                <Th></Th>
                              </Tr>
                            </thead>
                            <Tbody>
                              {getFilteredLots(pastSaleBids.groupedBids, false).map(([lot, lotData]) => {
                                const winningBid = lotData.bids.find(bid => bid.id === lotData.item.winning_bid);
                                const isWinner = winningBid !== undefined;
                                const topBid = getHighestValidBid(lotData.bids);
                                return (<Tr key={lot} className="lot-table">
                                  <Td>
                                    <ItemLinkChakra itemUuid={lotData.item.uuid} color="blue.500">
                                      {lot}
                                    </ItemLinkChakra>                                  
                                  </Td>
                                  <Td>{itemDetails[lotData.item.uuid]?.opening_price}</Td>                            
                                  <Td>
                                    <span className={isInvalidBid(topBid) ? 'line-through' : ''}>
                                      {formatCurrencyAmount(topBid?.amount || 0, pastSaleBids.sale.currency)}
                                    </span>
                                    {isInvalidBid(topBid) && 
                                      <Tooltip label={getInvalidReason(topBid)} hasArrow>
                                        <span className="deleted-label">{t('myBids.deleted')}</span>
                                      </Tooltip>
                                    }
                                    {isDuplicatedBid(topBid) && (
                                      <Tooltip label={getDuplicatedReason(topBid)} hasArrow>
                                        <span className="deleted-label">
                                          {t('myBids.duplicateBid')}
                                        </span>
                                      </Tooltip>
                                    )}
                                  </Td>
                                  <Td className="bidder-number">#{lotData.bids[0]?.bidder_id}</Td>
                                  <Td>
                                    {lotData.bids.find(b => b.type === 'mail') && (
                                      <GroupEditor
                                        bid={lotData.bids.find(b => b.type === 'mail')}
                                        item={lotData.item}
                                        onGroupUpdate={handleGroupUpdate}
                                        itemsBidData={itemsBidData}
                                      />
                                    )}
                                  </Td>
                                  <Td>{lotData.item.status === 'sold' ? formatCurrencyAmount(lotData.item.current_price, pastSaleBids.sale.currency) : '-'}</Td>
                                  <Td className="won-item">{lotData.item.status === 'sold' ? (isWinner ? t('myBids.youWonThisLot') : null) : lotData.item.status === 'unsold' ? t('myBids.unsold') : isWinner ? t('myBids.youAreHighestBidder') : '-'}</Td></Tr>
                                );
                              })}
                            </Tbody>
                          </Table>
                        </Box>
                      </Hide>
   
                      <Show below='md'>
                        <Stack spacing={4}>
                          {getFilteredLots(pastSaleBids.groupedBids, false).map(([lot, lotData]) => {
                            const winningBid = lotData.bids.find(bid => bid.id === lotData.item.winning_bid);
                            const isWinner = winningBid !== undefined;
                            const topBid = getHighestValidBid(lotData.bids);
                            
                            return (
                              <Card key={lot}>
                                <CardBody>
                                  <Stack divider={<StackDivider />} spacing={3}>
                                    <Flex justify="space-between" align="center">
                                      <ItemLinkChakra itemUuid={lotData.item.uuid} color="blue.500">
                                        {t('myBids.lot')} {lot}
                                      </ItemLinkChakra>
                                      <Text>#{topBid?.bidder_id}</Text>
                                    </Flex>
                                    
                                    <Flex justify="space-between" align="center">
                                      <Box fontWeight="medium">{t('myBids.openingPrice')}:</Box>
                                      <Box>{itemDetails[lotData.item.uuid]?.opening_price}</Box>
                                    </Flex>

                                    <Flex justify="space-between" align="center">
                                      <Box fontWeight="medium">{t('myBids.topBid')}:</Box>
                                      <Flex align="center" gap={2}>
                                        <span className={isInvalidBid(topBid) ? 'line-through' : ''}>
                                          {formatCurrencyAmount(topBid?.amount || 0, pastSaleBids.sale.currency)}
                                        </span>
                                        {isInvalidBid(topBid) && (
                                          <Tooltip label={getInvalidReason(topBid)} hasArrow>
                                            <span className="deleted-label">{t('myBids.deleted')}</span>
                                          </Tooltip>
                                        )}
                                        {isDuplicatedBid(topBid) && (
                                          <Tooltip label={getDuplicatedReason(topBid)} hasArrow>
                                            <span className="deleted-label">
                                              {t('myBids.duplicateBid')}
                                            </span>
                                          </Tooltip>
                                        )}
                                      </Flex>
                                    </Flex>

                                    <Flex justify="space-between" align="center">
                                      <Box fontWeight="medium">{t('myBids.soldFortable')}:</Box>
                                      <Box>{lotData.item.status === 'sold' ? formatCurrencyAmount(lotData.item.current_price, pastSaleBids.sale.currency) : '-'}</Box>
                                    </Flex>

                                    {(isWinner || lotData.item.status === 'unsold') && (
                                      <Box color="blue.500" textAlign="right" className="won-item">
                                        {lotData.item.status === 'sold' ? 
                                          (isWinner ? t('myBids.youWonThisLot') : null) : 
                                          lotData.item.status === 'unsold' ? 
                                            t('myBids.unsold') : 
                                            isWinner ? t('myBids.youAreHighestBidder') : '-'}
                                      </Box>
                                    )}
                                  </Stack>
                                </CardBody>
                              </Card>
                            );
                          })}
                        </Stack>
                      </Show>
                    </>
                  ) : (
                    getFilteredLots(pastSaleBids.groupedBids, false).map(([lot, lotData], lotIndex) => (
                      <Box key={lotIndex} className="lot-info" mb={4}>
                        <Box className="lot-number">
                          <ItemLinkChakra itemUuid={lotData.item.uuid} color="blue.500">
                            {t('myBids.lot')} {lot}
                          </ItemLinkChakra>
                        </Box>
                        <Box className="lot-price">
                          {t('myBids.openingBid', { amount: itemDetails[lotData.item.uuid]?.opening_price })}
                        </Box>
                        <Box className="current-price">
                          <LotInfo
                            lotData={lotData}
                            currency={pastSaleBids.sale.currency}
                          />
                        </Box>
                        <Box className="lot-image">
                          <ImageGallery imageGallery={itemDetails[lotData.item.uuid]?.image_gallery} />
                        </Box>
                        <Box className="lot-details">
                          <Box className="lot-title">
                            {itemDetails[lotData.item.uuid]?.has_title ? itemDetails[lotData.item.uuid]?.title : ''}
                          </Box>
                          <Box>
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(itemDetails[lotData.item.uuid]?.body) }}></div>
                          </Box>
                        </Box>
                        <Box className="lot-bids">
                        <Table>
                          <Tbody>
                            {(expandedLots[lotData.item.uuid] ? lotData.bids : lotData.bids.slice(0, 3)).map((bid, bidIndex) => (
                              <Tr>
                                <Td>
                                  <span className={isInvalidBid(bid) ? 'line-through' : ''}>
                                    {formatCurrencyAmount(bid.amount, pastSaleBids.sale.currency)}
                                  </span>
                                  {isInvalidBid(bid) && (
                                    <Tooltip label={getInvalidReason(bid)} hasArrow>
                                      <span className="deleted-label">{t('myBids.deleted')}</span>
                                    </Tooltip>
                                  )}
                                  {isDuplicatedBid(bid) && (
                                    <Tooltip label={getDuplicatedReason(bid)} hasArrow>
                                      <span className="deleted-label">
                                        {t('myBids.duplicateBid')}
                                      </span>
                                    </Tooltip>
                                  )}                                 
                                </Td>
                                <Td className="bid-type">
                                    ({formatBidType(bid.type)} #{bid.bidder_id})
                                    &nbsp;
                                  <span className="bid-date">{formatDate(bid.created)}</span>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                        {lotData.bids.length > 3 && (
                            <Box onClick={() => setExpandedLots(prev => ({...prev, [lotData.item.uuid]: !prev[lotData.item.uuid]}))} 
                                cursor="pointer" 
                                textAlign="center" 
                                mt={2}>
                              {expandedLots[lotData.item.uuid] ? 
                                <>{t('myBids.close')} <ArrowUpIcon /></> : 
                                <>{t('myBids.showMore')} <ArrowDownIcon /></>
                              }
                            </Box>
                          )}
                          <Box textAlign="center">
                            {lotData.bids.find(b => b.type === 'mail') && (
                              <GroupEditor
                                bid={lotData.bids.find(b => b.type === 'mail')}
                                item={lotData.item}
                                onGroupUpdate={handleGroupUpdate}
                                itemsBidData={itemsBidData}
                              />
                            )}
                          </Box>
                        
                          
                        </Box>
                      </Box>
                    ))
                   
                  )}
                  
                  {selectedPastSale && pastSaleBids && pastSaleBids.sale.sale_status !== 'closed_not_started' && (
                    <MyCredit
                      accessToken={config.accessToken}
                      userUuid={userData.uuid}
                      saleUuid={pastSaleBids.sale.sale_uuid}
                      currency={pastSaleBids.sale.currency}
                    />
                  )}
                  <MyMaxBidLimit
                      accessToken={config.accessToken}
                      saleUuid={pastSaleBids.sale.sale_uuid}
                      userUuid={userData.uuid}
                  />
                </Box>
              ) : null}
            </TabPanel>
          </TabPanels>
        </Tabs>
        <div className="bottom-text">
          {t('myBids.clickOnSaleName')} <ArrowUpIcon />
        </div>
      </div>
    </>
  );
};
export default MyBids;
